import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import {
  Mic,
  Camera,
  Sparkles,
  CheckCircle2,
  Lock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

/**
 * First-run onboarding tour. 4 short cards that introduce the
 * concepts: capture signals → review the AI draft → approve →
 * private by default. Each card has an icon, a one-liner, and a
 * mini illustration via lucide icons.
 *
 * State: persisted in localStorage (per device) rather than the DB
 * because the tour is purely informational and device-specific —
 * different from the Disclosure + RegionSetup modals which are
 * compliance / configuration events recorded server-side.
 *
 * Visibility:
 *   - Fires once `disclosureAckedAt` AND `regionSetupAckedAt` are
 *     both non-null AND localStorage has no `receipt:tour:done` key.
 *   - Skippable any time via the X / "Skip tour" button. Skip is
 *     treated the same as completion — we don't nag.
 */
const STORAGE_KEY = "receipt:tour:done";

export function OnboardingTour({
  prerequisitesMet,
}: {
  /** True iff the prior modals (Disclosure + RegionSetup) are acked.
   * Tour stays mounted but hidden until this flips, so the user never
   * sees three modals stacked. */
  prerequisitesMet: boolean;
}) {
  const { locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!prerequisitesMet) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;
    // Small defer so the dashboard renders behind the modal first —
    // gives the user context for what they're about to learn about.
    const t = window.setTimeout(() => setOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [prerequisitesMet]);

  // Allow re-opening the tour on demand (e.g. the Home capture-first
  // "Learn how receipts work" link), even after it was completed/skipped.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reopen = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener("receipt:open-tour", reopen);
    return () => window.removeEventListener("receipt:open-tour", reopen);
  }, []);

  function dismiss() {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "1");
    }
    setOpen(false);
  }

  const steps = [
    {
      icon: <Sparkles className="h-8 w-8 text-coral" />,
      title:
        locale === "ar"
          ? "مرحباً بك في ريتايف"
          : "Welcome to Retayf",
      body:
        locale === "ar"
          ? "نختصر أسبوع عملك في إيصالات يومية وأسبوعية، تراجعها وتعتمدها بنقرة واحدة. كلّ شيء يبدأ بـ ’إشارة‘: نشاط قمت به."
          : "We turn your week into daily and weekly receipts you review with one tap. Everything starts with a ‘signal’: something you did.",
    },
    {
      icon: (
        <div className="flex gap-3">
          <Mic className="h-8 w-8 text-coral" />
          <Camera className="h-8 w-8 text-coral" />
        </div>
      ),
      title:
        locale === "ar" ? "التقط عملك بسرعة" : "Capture your work fast",
      body:
        locale === "ar"
          ? "اضغط زر ＋ في منتصف الشريط السفلي لتدوين ملاحظة، أو تسجيل صوتي، أو التقاط صورة. نرتّبها لك كإنجازات وأعمال منجزة وعوائق. تراجع كلّ شيء قبل الحفظ."
          : "Tap the ＋ button in the middle of the bottom bar to jot a note, record a voice memo, or snap a photo. We organize them into Highlights, Shipped, and Blockers. You review everything before it's saved.",
    },
    {
      icon: <CheckCircle2 className="h-8 w-8 text-emerald-600" />,
      title:
        locale === "ar"
          ? "اعتمد الإيصال اليومي"
          : "Approve the daily receipt",
      body:
        // NB: do not promise automatic fixed-time end-of-day drafting —
        // that behavior isn't guaranteed for all cases. Frame it as "helps
        // prepare a draft" + the approve/share trust guarantee.
        locale === "ar"
          ? "التقط طوال اليوم. يساعدك ريتايف في تجهيز مسودّة إيصالك. افتحها، عدّل إن لزم، ثم اعتمدها. لا يُشارَك شيء حتى تعتمده، وإيصالك الأسبوعي يُبنى من أيامك المعتمدة."
          : "Capture throughout the day. Retayf helps prepare a draft. Open it, edit if needed, then approve. Nothing is shared until you approve it, and your weekly receipt builds from your approved days.",
    },
    {
      icon: <Lock className="h-8 w-8 text-coral" />,
      title:
        locale === "ar"
          ? "خاص افتراضياً"
          : "Private by default",
      body:
        locale === "ar"
          ? "كل ما تلتقطه ومسودّاتك تبقى لك وحدك. لا يرى فريقك إلا الإيصالات التي تعتمدها بنفسك، وبمستوى التفاصيل الذي تختاره في الإعدادات. يمكنك التراجع عن المشاركة في أيّ وقت."
          : "Everything you capture and every draft stays yours alone. Your team only sees receipts you approve yourself, at the level of detail you choose in Settings. You can unshare at any time.",
    },
  ];

  const cur = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={o => !o && dismiss()}>
      <DialogContent
        showCloseButton={false}
        className="
          sm:max-w-md
          max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0
          max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl
          p-0 overflow-hidden
        "
      >
        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        <div className="px-6 pt-5 pb-6 flex flex-col items-center text-center gap-3">
          <div className="rounded-full bg-coral-soft/40 p-4 mb-1">
            {cur.icon}
          </div>
          <DialogTitle className="font-display text-xl">
            {cur.title}
          </DialogTitle>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {cur.body}
          </p>

          {/* Step dots */}
          <div className="flex gap-1.5 mt-2">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? "bg-coral w-5"
                    : "bg-muted-foreground/30 w-1.5"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-border/60 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {locale === "ar" ? "تخطّي" : "Skip"}
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(s => s - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {locale === "ar" ? "السابق" : "Back"}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => (isLast ? dismiss() : setStep(s => s + 1))}
              className="bg-coral hover:bg-coral/90 text-white"
            >
              {isLast
                ? locale === "ar"
                  ? "ابدأ"
                  : "Get started"
                : locale === "ar"
                  ? "التالي"
                  : "Next"}
              {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
